import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../config";

// SankalpTaru's page for this entry's trees: the direct link they sent back
// (2024 list), else a name search on the BPlusWithBhaskar forest page.
const plantsUrl = (c) => {
  if (c.partner_url) return c.partner_url;
  if (c.payment_status !== "planted" || !c.dedicated_to) return null;
  const firstWord = c.dedicated_to.trim().split(/[,&]/)[0].trim().split(/\s+/)[0];
  return `https://sankalptaru.org/c/bpluswithbhaskar/?search_by=name&search=${encodeURIComponent(firstWord)}`;
};

// Our "from space" page for this entry: its own farm when we know which farm its
// SankalpTaru tree is on, otherwise the overview of the farms for that year.
const spaceUrl = (c, treeToFarm) => {
  const treeId = (c.partner_url || "").match(/sankalptaru\.org\/tree\/([^/?#]+)/)?.[1];
  if (treeId && treeToFarm[treeId]) return `/farm/${treeToFarm[treeId]}`;
  return c.year === 2024 ? "/farms" : null;
};

const PAID = ["paid", "zelle_paid", "gpay_paid"];
const statusOf = (c) => (c.payment_status === "planted" ? "Planted" : PAID.includes(c.payment_status) ? "Paid" : "Pledged");
const STATUS_STYLE = {
  Planted: "bg-green-100 text-green-800",
  Paid: "bg-blue-100 text-blue-800",
  Pledged: "bg-yellow-100 text-yellow-800",
};

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

// One card per person: donor_id (db/sritaru-donor-id.sql) says which rows are the same donor.
// Without it each contribution keeps its own card: first names alone would merge different people.
const donorKey = (c) => c.donor_id || `row:${c.contribution_id}`;

// One contribution = one donor's rows from the same day (undated 2024 partner rows: the same year).
// Bhaskar, 2026-11-02: Sree Karuna's 2024 gift was one contribution dedicated to many names.
const giftKey = (c) => (c.created_at ? c.created_at.slice(0, 10) : `year:${c.year || ""}`);

const countByStatus = (items) => {
  const counts = {};
  items.forEach((c) => {
    const s = statusOf(c);
    counts[s] = (counts[s] || 0) + (c.num_trees || 0);
  });
  return counts;
};

function groupGifts(items) {
  const byKey = new Map();
  items.forEach((c) => {
    const k = giftKey(c);
    if (!byKey.has(k)) byKey.set(k, []);
    byKey.get(k).push(c);
  });
  return [...byKey.entries()].map(([key, rows]) => ({
    key,
    rows,
    first: rows[0],
    trees: rows.reduce((s, c) => s + (c.num_trees || 0), 0),
    counts: countByStatus(rows),
  }));
}

function groupDonors(rows) {
  const byKey = new Map();
  rows.forEach((c) => {
    const k = donorKey(c);
    if (!byKey.has(k)) byKey.set(k, []);
    byKey.get(k).push(c);
  });
  return [...byKey.entries()].map(([key, items]) => {
    // rows arrive newest first (undated last), so items[0] is the latest
    const years = [...new Set(items.map((c) => c.year).filter(Boolean))].sort();
    const counts = countByStatus(items);
    const links = [...new Set(items.map(plantsUrl).filter(Boolean))];
    return {
      key,
      name: items[0].first_name,
      teams: [...new Set(items.map((c) => c.team).filter(Boolean))],
      items,
      gifts: groupGifts(items),
      trees: items.reduce((s, c) => s + (c.num_trees || 0), 0),
      years,
      counts,
      // one link for all of them (or only one entry has a link): show it once on the card
      sharedLink: links.length === 1 && items.filter(plantsUrl).length === items.length ? links[0] : null,
    };
  });
}

function StatusBadge({ status, trees }) {
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${STATUS_STYLE[status]}`}>
      {trees != null ? `${trees.toLocaleString()} ${status.toLowerCase()}` : status}
    </span>
  );
}

function EntryLinks({ c, treeToFarm, showPlants = true }) {
  const plants = showPlants && plantsUrl(c);
  const space = spaceUrl(c, treeToFarm);
  return (
    <>
      {plants && (
        <a href={plants} target="_blank" rel="noopener noreferrer"
           className="text-[10px] text-green-700 hover:text-green-900 underline font-semibold">
          View your plants
        </a>
      )}
      {space && (
        <Link to={space} className="text-[10px] text-sky-700 hover:text-sky-900 underline font-semibold">
          🛰️ From space
        </Link>
      )}
    </>
  );
}

function DonorCard({ d, treeToFarm, open, onToggle }) {
  const multi = d.items.length > 1;
  const one = d.items[0];
  const manyGifts = d.gifts.length > 1;
  return (
    <div className={`bg-white rounded-xl shadow-sm border p-5 hover:shadow-md transition-shadow ${
      open ? "border-sritaru-300 sm:col-span-2 lg:col-span-3" : "border-sritaru-100"}`}>
      <div className="flex justify-between items-start mb-2 gap-2">
        <p className="font-semibold text-sritaru-800">{d.name}</p>
        <div className="flex flex-wrap gap-1 justify-end">
          {d.teams.map((t) => (
            <span key={t} className="bg-sritaru-100 text-sritaru-700 text-xs px-2 py-1 rounded-full font-medium">{t}</span>
          ))}
        </div>
      </div>
      <p className="text-gray-700 text-sm">
        contributed <span className="font-bold text-sritaru-600">{d.trees.toLocaleString()}</span> &#127795;
        {manyGifts ? (
          <span className="text-gray-500">
            {" "}in {d.gifts.length} contributions
            {d.years.length > 0 && <> · {d.years.length > 1 ? `${d.years[0]}–${d.years[d.years.length - 1]}` : d.years[0]}</>}
          </span>
        ) : multi && (
          <span className="text-gray-500">
            {" "}in {d.items.length} names{d.years.length > 0 && <> · {d.years[0]}</>}
          </span>
        )}
      </p>

      {!multi ? (
        <>
          {one.dedicated_to && <p className="text-gray-500 text-xs mt-1 italic">dedicated to {one.dedicated_to}</p>}
          <div className="flex items-center gap-2 mt-2">
            <StatusBadge status={statusOf(one)} />
            <EntryLinks c={one} treeToFarm={treeToFarm} />
            <span className="text-gray-400 text-[10px] ml-auto">{formatDate(one.created_at)}</span>
          </div>
        </>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            {["Planted", "Paid", "Pledged"].filter((s) => d.counts[s]).map((s) => (
              <StatusBadge key={s} status={s} trees={d.counts[s]} />
            ))}
            {d.sharedLink && (
              <a href={d.sharedLink} target="_blank" rel="noopener noreferrer"
                 className="text-[10px] text-green-700 hover:text-green-900 underline font-semibold">
                View your plants
              </a>
            )}
            <button type="button" onClick={onToggle} aria-expanded={open}
                    className="ml-auto text-xs font-semibold text-sritaru-700 hover:text-sritaru-900 bg-sritaru-50 hover:bg-sritaru-100 rounded-full px-3 py-1">
              {open ? "Hide ▴" : manyGifts ? `Show all ${d.gifts.length} ▾` : `Show ${d.items.length} names ▾`}
            </button>
          </div>
          {open && (
            <ol className="mt-4 border-l-2 border-sritaru-200 ml-1 space-y-3">
              {d.gifts.map((g) => {
                const c = g.first;
                const single = g.rows.length === 1;
                const statuses = Object.keys(g.counts);
                return (
                  <li key={g.key} className="relative pl-4">
                    <span className="absolute -left-[7px] top-1.5 w-3 h-3 rounded-full bg-sritaru-400 border-2 border-white" />
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      <span className="text-xs text-gray-500 w-24 shrink-0">
                        {c.created_at ? formatDate(c.created_at) : c.year || ""}
                      </span>
                      <span className="text-sm text-gray-800">
                        <span className="font-bold text-sritaru-600">{g.trees.toLocaleString()}</span> &#127795;
                        {!single && <span className="text-gray-500 text-xs"> for {g.rows.length} names</span>}
                      </span>
                      {statuses.length === 1 ? <StatusBadge status={statuses[0]} />
                        : statuses.map((s) => <StatusBadge key={s} status={s} trees={g.counts[s]} />)}
                      {single && <EntryLinks c={c} treeToFarm={treeToFarm} showPlants={!d.sharedLink} />}
                    </div>
                    {single ? c.dedicated_to && (
                      <p className="text-gray-500 text-xs italic mt-0.5 sm:ml-[108px]">dedicated to {c.dedicated_to}</p>
                    ) : (
                      <ul className="mt-1 sm:ml-[108px] space-y-0.5">
                        {g.rows.map((r, i) => (
                          <li key={r.contribution_id || i} className="flex flex-wrap items-center gap-x-3 text-xs">
                            <span className="text-gray-500 italic">
                              {r.dedicated_to ? `dedicated to ${r.dedicated_to}` : "—"}
                              {r.num_trees > 1 && <span className="not-italic"> · {r.num_trees} &#127795;</span>}
                            </span>
                            <EntryLinks c={r} treeToFarm={treeToFarm} showPlants={!d.sharedLink} />
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ol>
          )}
        </>
      )}
    </div>
  );
}

function Wall() {
  const [contributions, setContributions] = useState([]);
  const [treeToFarm, setTreeToFarm] = useState({});
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("all"); // "all" or "team"
  const [filterTeam, setFilterTeam] = useState("");
  const [openKey, setOpenKey] = useState(null);

  useEffect(() => {
    fetch("/space/index.json")
      .then((r) => (r.ok ? r.json() : null))
      .then((idx) => idx && setTreeToFarm(idx.tree_to_farm || {}))
      .catch(() => {});
  }, []);

  useEffect(() => {
    async function fetchData() {
      try {
        const cols = "contribution_id, first_name, num_trees, dedicated_to, payment_status, year, created_at, partner_url, sritaru_teams(team_name)";
        const query = (c) => supabase.from("sritaru_contributions").select(c)
          .order("created_at", { ascending: false, nullsFirst: false });
        let { data: contribs, error } = await query(`donor_id, ${cols}`);
        if (error) ({ data: contribs } = await query(cols));   // before db/sritaru-donor-id.sql

        // Normalize team name from join
        const normalized = (contribs || []).map(c => ({
          ...c,
          team: c.sritaru_teams?.team_name || null,
        }));
        setContributions(normalized);

        // Extract unique teams
        const uniqueTeams = [
          ...new Set(normalized.map((c) => c.team).filter(Boolean)),
        ].sort();
        setTeams(uniqueTeams);
      } catch (err) {
        console.error("Error fetching contributions:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  const totalTrees = contributions.reduce(
    (sum, c) => sum + (c.num_trees || 0),
    0
  );

  const filtered =
    filterTeam
      ? contributions.filter((c) => c.team === filterTeam)
      : contributions;

  const donors = useMemo(() => groupDonors(filtered), [filtered]);
  const allDonors = useMemo(() => groupDonors(contributions), [contributions]);
  const giftCount = allDonors.reduce((n, d) => n + d.gifts.length, 0);

  const grouped = {};
  if (viewMode === "team") {
    filtered.forEach((c) => {
      const t = c.team || "No Team";
      if (!grouped[t]) grouped[t] = [];
      grouped[t].push(c);
    });
  }

  const card = (d, scope) => {
    const k = `${scope}:${d.key}`;
    return (
      <DonorCard key={k} d={d} treeToFarm={treeToFarm} open={openKey === k}
                 onToggle={() => setOpenKey(openKey === k ? null : k)} />
    );
  };

  return (
    <div className="py-10 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Hero card */}
        <div className="bg-gradient-to-r from-sritaru-600 to-sritaru-700 text-white rounded-xl shadow-lg p-8 mb-8 text-center">
          <h1 className="text-3xl sm:text-4xl font-bold mb-2">
            Contributor Wall &#127795;
          </h1>
          <p className="text-5xl font-bold mt-4">
            {totalTrees.toLocaleString()}
          </p>
          <p className="text-sritaru-200 text-lg">total trees contributed</p>
          <p className="text-sritaru-300 text-sm mt-2">
            {allDonors.length} contributor{allDonors.length !== 1 ? "s" : ""} ·{" "}
            {giftCount} contribution{giftCount !== 1 ? "s" : ""}
          </p>
        </div>

        {/* Controls */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6 items-center justify-between">
          <div className="flex gap-2">
            <button
              onClick={() => setViewMode("all")}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                viewMode === "all"
                  ? "bg-sritaru-600 text-white"
                  : "bg-sritaru-100 text-sritaru-700 hover:bg-sritaru-200"
              }`}
            >
              All
            </button>
            <button
              onClick={() => setViewMode("team")}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                viewMode === "team"
                  ? "bg-sritaru-600 text-white"
                  : "bg-sritaru-100 text-sritaru-700 hover:bg-sritaru-200"
              }`}
            >
              By Team
            </button>
          </div>
          <select
            value={filterTeam}
            onChange={(e) => setFilterTeam(e.target.value)}
            className="border border-gray-300 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-sritaru-400 outline-none"
          >
            <option value="">All Teams</option>
            {teams.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            No contributions yet. Be the first!
          </div>
        ) : viewMode === "all" ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {donors.map((d) => card(d, "all"))}
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(grouped)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([team, members]) => {
                const teamTotal = members.reduce(
                  (s, m) => s + (m.num_trees || 0),
                  0
                );
                return (
                  <div key={team}>
                    <div className="flex items-center gap-3 mb-4">
                      <h3 className="text-xl font-bold text-sritaru-700">
                        {team}
                      </h3>
                      <span className="bg-sritaru-600 text-white text-xs px-3 py-1 rounded-full">
                        {teamTotal.toLocaleString()} trees
                      </span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {groupDonors(members).map((d) => card(d, team))}
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </div>
    </div>
  );
}

export default Wall;
